import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Lightning, Crown, ShoppingBag, Sparkle, Robot, Tag, ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_BADGE = {
  owner: { label: '👑 Owner', className: 'bg-gradient-to-r from-amber-500 to-pink-500 text-white' },
  starter: { label: '🌱 Starter', className: 'bg-emerald-100 text-emerald-700' },
  pro: { label: '🚀 Pro', className: 'bg-blue-100 text-blue-700' },
  agency_s: { label: '🏢 Agency S', className: 'bg-purple-100 text-purple-700' },
  agency_m: { label: '🏢 Agency M', className: 'bg-purple-100 text-purple-700' },
  agency_l: { label: '🏢 Agency L', className: 'bg-purple-100 text-purple-700' },
};

const KIND_LABELS = {
  product: { label: 'Produkter', icon: ShoppingBag },
  category: { label: 'Kategorier', icon: Tag },
  term: { label: 'Attributt-termer', icon: Tag },
  reopt: { label: 'Re-optimaliseringer (½ pris)', icon: Sparkle },
  post: { label: 'Blogginnlegg', icon: Robot },
};

export default function UsagePage({ embedded = false }) {
  const [data, setData] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    [],
  );

  const load = useCallback(async () => {
    try {
      const [u, t] = await Promise.all([
        axios.get(`${API}/api/billing/usage`, { headers: getHeaders() }),
        axios.get(`${API}/api/billing/tiers`),
      ]);
      setData(u.data);
      setTiers(t.data.tiers);
      setPacks(t.data.topup_packs);
    } catch (err) {
      toast.error('Kunne ikke hente forbruk');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="h-64 bg-muted animate-pulse rounded-md" />;
  if (!data) return null;

  const tier = data.tier;
  const isUnlimited = data.is_unlimited;
  const customerQuota = data.monthly_quota_customer_facing;
  const used = Math.round(data.units_used);
  const remaining = data.remaining ?? null;
  const usedPct = !isUnlimited && customerQuota
    ? Math.min(100, Math.round((used / customerQuota) * 100))
    : 0;
  const isWarn = !isUnlimited && usedPct >= 80;
  const isExhausted = !isUnlimited && (remaining ?? 0) <= 0;

  return (
    <div className="space-y-6" data-testid="usage-page">
      {/* Header — hidden when embedded inside Settings tab */}
      {!embedded && (
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-4xl tracking-tight font-extrabold flex items-center gap-3">
              <Lightning size={36} weight="bold" /> Forbruk & abonnement
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Oversikt over hvor mange optimaliseringer du har brukt denne måneden.
            </p>
          </div>
          <Badge className={`text-sm px-3 py-1 ${TIER_BADGE[tier]?.className || ''}`}>
            {TIER_BADGE[tier]?.label || tier}
          </Badge>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end">
          <Badge className={`text-sm px-3 py-1 ${TIER_BADGE[tier]?.className || ''}`}>
            {TIER_BADGE[tier]?.label || tier}
          </Badge>
        </div>
      )}

      {/* Quota card */}
      <Card
        className={`border-2 ${
          isExhausted ? 'border-destructive bg-destructive/5'
            : isWarn ? 'border-amber-400 bg-amber-50'
              : 'border-border'
        }`}
        data-testid="quota-card"
      >
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            {isUnlimited ? <Crown weight="bold" className="text-amber-500" /> : <Lightning weight="bold" />}
            {isUnlimited ? 'Ubegrenset bruk' : 'Denne måneden'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isUnlimited ? (
            <div>
              <p className="font-heading text-4xl font-extrabold">{used.toLocaleString('nb-NO')}</p>
              <p className="text-sm text-muted-foreground mt-1">
                optimaliseringer kjørt denne måneden ({data.ym})
              </p>
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-100 text-amber-800 text-xs">
                <Crown size={14} weight="bold" />
                Owner-tier — ingen grenser
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <p className="font-heading text-4xl font-extrabold">
                  {used.toLocaleString('nb-NO')}
                  <span className="text-xl text-muted-foreground font-normal"> / {customerQuota?.toLocaleString('nb-NO')}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  {(remaining ?? 0).toLocaleString('nb-NO')} igjen
                </p>
              </div>
              <div className="mt-3 h-3 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${
                    isExhausted ? 'bg-destructive' : isWarn ? 'bg-amber-500' : 'bg-primary'
                  }`}
                  style={{ width: `${usedPct}%` }}
                />
              </div>
              <p className={`text-xs mt-2 ${isExhausted ? 'text-destructive font-medium' : isWarn ? 'text-amber-700' : 'text-muted-foreground'}`}>
                {isExhausted
                  ? 'Du har brukt opp månedens kvote. Kjøp flere eller oppgrader plan.'
                  : isWarn
                    ? `Du har brukt ${usedPct}% — vurder å oppgradere eller kjøpe ekstra.`
                    : `Brukt ${usedPct}% av månedens kvote.`}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Breakdown by kind */}
      {Object.keys(data.by_kind || {}).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Fordeling</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(data.by_kind).map(([kind, n]) => {
              const meta = KIND_LABELS[kind] || { label: kind, icon: ShoppingBag };
              const Icon = meta.icon;
              return (
                <div key={kind} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <Icon size={16} className="text-muted-foreground" />
                    <span className="text-sm">{meta.label}</span>
                  </div>
                  <span className="font-mono text-sm">{n.toLocaleString('nb-NO')}</span>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Top-up packs */}
      {!isUnlimited && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              💎 Kjøp ekstra optimaliseringer
            </CardTitle>
            <p className="text-xs text-muted-foreground">Engangskjøp — legges til denne måneden. Tilgjengelig for alle tier.</p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {packs.map((p) => (
              <Card key={p.id} className="border hover:border-primary transition-colors" data-testid={`topup-${p.id}`}>
                <CardContent className="p-4 text-center">
                  <p className="font-heading text-2xl font-extrabold">{p.units.toLocaleString('nb-NO')}</p>
                  <p className="text-xs text-muted-foreground mb-2">optimaliseringer</p>
                  <p className="font-bold text-primary">kr {p.price_nok}</p>
                  <p className="text-xs text-muted-foreground">
                    kr {(p.price_nok / p.units).toFixed(2)}/stk
                  </p>
                  <Button size="sm" className="w-full mt-3" disabled>
                    Kommer snart
                  </Button>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Upgrade CTA */}
      {!isUnlimited && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Oppgrader plan</CardTitle>
            <p className="text-xs text-muted-foreground">Få flere optimaliseringer per måned + raskere kjøring.</p>
          </CardHeader>
          <CardContent>
            <Button asChild className="gap-2" data-testid="upgrade-link">
              <Link to="/pricing">Se planer <ArrowRight size={14} /></Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
